import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HiOutlineExclamationTriangle,
  HiOutlineShieldExclamation,
  HiOutlineArrowRight,
} from 'react-icons/hi2';

const severityConfig = {
  armageddon: {
    bg: 'bg-red-50',
    border: 'border-red-300',
    text: 'text-red-800',
    badge: 'bg-red-600',
    label: 'CRITICAL',
  },
  critical: {
    bg: 'bg-red-50',
    border: 'border-red-300',
    text: 'text-red-800',
    badge: 'bg-red-500',
    label: 'Critical',
  },
  warning: {
    bg: 'bg-amber-50',
    border: 'border-amber-300',
    text: 'text-amber-800',
    badge: 'bg-amber-500',
    label: 'Warning',
  },
  error: {
    bg: 'bg-orange-50',
    border: 'border-orange-300',
    text: 'text-orange-800',
    badge: 'bg-orange-500',
    label: 'Error',
  },
};

const getSeverity = (alert) => {
  if (alert.source === 'errorDetection') {
    return severityConfig[alert.severityLevel] || severityConfig.warning;
  }
  if (alert.streakDay >= 5) return severityConfig.critical;
  return severityConfig.warning;
};

const AlertBanner = ({ alerts = [] }) => {
  const navigate = useNavigate();

  if (!alerts || alerts.length === 0) return null;

  const riAlerts = alerts.filter((a) => a.source === 'routeIntelligence');
  const edAlerts = alerts.filter((a) => a.source === 'errorDetection');
  const hasCritical = alerts.some(
    (a) => a.severityLevel === 'critical' || a.severityLevel === 'armageddon' || a.streakDay >= 5
  );

  const bannerStyle = hasCritical ? 'bg-red-50 border-red-300' : 'bg-amber-50 border-amber-300';

  const iconColor = hasCritical ? 'text-red-500' : 'text-amber-500';

  return (
    <div className={`${bannerStyle} border-2 rounded-xl overflow-hidden mb-4 sm:mb-6 shadow-sm`}>
      <div className="px-3 sm:px-5 py-3 sm:py-4">
        <div className="flex items-start gap-3">
          <div className={`flex-shrink-0 mt-0.5 ${iconColor}`}>
            <HiOutlineExclamationTriangle className="w-5 h-5 sm:w-6 sm:h-6" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <h3
                className={`text-sm font-bold ${hasCritical ? 'text-red-900' : 'text-amber-900'}`}
              >
                Active Alerts
              </h3>
              <span
                className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold text-white ${hasCritical ? 'bg-red-500' : 'bg-amber-500'}`}
              >
                {alerts.length}
              </span>
            </div>

            <div className="space-y-1.5">
              {riAlerts.length > 0 && (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1">
                    <HiOutlineShieldExclamation className="w-3.5 h-3.5" />
                    Route Intelligence
                  </span>
                  {riAlerts.slice(0, 4).map((alert) => {
                    const sev = getSeverity(alert);
                    return (
                      <button
                        key={`ri-${alert.id}`}
                        onClick={() => navigate('/route-intelligence')}
                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-surface/70 border border-slate-200 hover:border-primary-300 hover:bg-surface transition-all text-xs group"
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${sev.badge} flex-shrink-0`} />
                        <span className="font-medium text-slate-700 truncate max-w-[140px]">
                          {alert.name}
                        </span>
                        <span className="text-slate-400">
                          {alert.variancePercent != null
                            ? `${Math.abs(alert.variancePercent).toFixed(1)}%`
                            : ''}
                          {alert.streakDay > 1 ? ` · ${alert.streakDay}d` : ''}
                        </span>
                        <HiOutlineArrowRight className="w-3 h-3 text-slate-400 group-hover:text-primary-500 transition-colors" />
                      </button>
                    );
                  })}
                  {riAlerts.length > 4 && (
                    <button
                      onClick={() => navigate('/route-intelligence')}
                      className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                    >
                      +{riAlerts.length - 4} more
                    </button>
                  )}
                </div>
              )}

              {edAlerts.length > 0 && (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1">
                    <HiOutlineExclamationTriangle className="w-3.5 h-3.5" />
                    Error Detection
                  </span>
                  {edAlerts.slice(0, 4).map((alert) => {
                    const sev = getSeverity(alert);
                    return (
                      <button
                        key={`ed-${alert.id}`}
                        onClick={() => navigate('/error-detection')}
                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-surface/70 border border-slate-200 hover:border-primary-300 hover:bg-surface transition-all text-xs group"
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${sev.badge} flex-shrink-0`} />
                        <span className="font-medium text-slate-700 truncate max-w-[140px]">
                          {alert.name}
                        </span>
                        <span className="text-slate-400">
                          {alert.severityLevel ? sev.label : ''}
                          {alert.streakDay > 1 ? ` · ${alert.streakDay}d` : ''}
                          {alert.errorCount ? ` · ${alert.errorCount} errors` : ''}
                        </span>
                        <HiOutlineArrowRight className="w-3 h-3 text-slate-400 group-hover:text-primary-500 transition-colors" />
                      </button>
                    );
                  })}
                  {edAlerts.length > 4 && (
                    <button
                      onClick={() => navigate('/error-detection')}
                      className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                    >
                      +{edAlerts.length - 4} more
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertBanner;
